import React from 'react'
import { Box, Button, Divider, Drawer, Stack } from '@mui/material'
import { shapeBorderRadius } from '../../theme/helpers'
import {
  fetchPrepItemDetail,
  type PrepDetailResponse,
  type PrepListItem,
} from '../../api/prep'
import PrepDetailContent from './PrepDetailContent'

export type PrepDetailDrawerProps = {
  open: boolean
  onClose: () => void
  prepId: number | null
  cached?: PrepDetailResponse | null
  fallbackItem?: PrepListItem | null
  enableMyPrepActions?: boolean
  onToggleMyPrep?: (next: boolean) => Promise<void>
  onDismiss?: () => Promise<void>
  onMarkUsed?: () => Promise<void>
  onArchive?: () => Promise<void>
  onAddToShowClock?: (params: { prepId: number; prepHeadline: string; launchedFromMyPrep: boolean }) => void
  launchedFromMyPrep?: boolean
  isSuperadmin?: boolean
  emptyContent?: React.ReactNode
  onDetailLoaded?: (detail: PrepDetailResponse) => void
  onRefreshCategories?: (categoryIds: number[]) => void
  navIds?: number[]
  onNavigate?: (prepId: number) => void
}

const PrepDetailDrawer: React.FC<PrepDetailDrawerProps> = ({
  open,
  onClose,
  prepId,
  cached = null,
  fallbackItem = null,
  enableMyPrepActions = true,
  onToggleMyPrep,
  onDismiss,
  onMarkUsed,
  onArchive,
  onAddToShowClock,
  launchedFromMyPrep = false,
  isSuperadmin = false,
  emptyContent,
  onDetailLoaded,
  onRefreshCategories,
  navIds = [],
  onNavigate,
}) => {
  const [detail, setDetail] = React.useState<PrepDetailResponse | null>(cached)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const lastFocusedRef = React.useRef<HTMLElement | null>(null)
  const lastScrollPositionRef = React.useRef({ left: 0, top: 0 })
  const wasOpenRef = React.useRef(false)

  React.useLayoutEffect(() => {
    if (!open || wasOpenRef.current || typeof document === 'undefined') {
      return
    }

    // Capture the opener before the Drawer's passive focus-trap effect can move focus.
    const active = document.activeElement
    if (active instanceof HTMLElement) {
      lastFocusedRef.current = active
    }
    lastScrollPositionRef.current = {
      left: window.scrollX,
      top: window.scrollY,
    }
  }, [open])

  React.useEffect(() => {
    if (open) {
      wasOpenRef.current = true
      return
    }

    if (wasOpenRef.current) {
      wasOpenRef.current = false
      const target = lastFocusedRef.current
      const scrollPosition = lastScrollPositionRef.current
      const restoreFocusWithoutScrolling = () => {
        if (target?.isConnected && typeof target.focus === 'function') {
          target.focus({ preventScroll: true })
        }

        if (window.scrollX !== scrollPosition.left || window.scrollY !== scrollPosition.top) {
          window.scrollTo({
            left: scrollPosition.left,
            top: scrollPosition.top,
            behavior: 'auto',
          })
        }
      }
      if (typeof queueMicrotask === 'function') {
        queueMicrotask(restoreFocusWithoutScrolling)
      } else {
        setTimeout(restoreFocusWithoutScrolling, 0)
      }
    }
  }, [open])

  React.useEffect(() => {
    if (!prepId) {
      setDetail(null)
      return
    }
    if (cached && cached.id === prepId) {
      setDetail(cached)
      return
    }
    setDetail((current) => (current && current.id === prepId ? current : null))
  }, [cached, prepId])

  React.useEffect(() => {
    if (!open || !prepId) {
      setLoading(false)
      setError(null)
      return
    }

    let mounted = true
    const controller = new AbortController()

    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const response = await fetchPrepItemDetail(prepId, { signal: controller.signal })
        if (!mounted) {
          return
        }
        setDetail(response)
        onDetailLoaded?.(response)
      } catch (error_) {
        if (controller.signal.aborted || !mounted) {
          return
        }
        const message =
          error_ && typeof error_ === 'object' && 'message' in error_
            ? String((error_ as { message?: unknown }).message ?? 'Failed to load prep item')
            : 'Failed to load prep item'
        setError(message)
      } finally {
        if (!controller.signal.aborted && mounted) {
          setLoading(false)
        }
      }
    }

    void load()

    return () => {
      mounted = false
      controller.abort()
    }
  }, [open, prepId, onDetailLoaded])

  const activeItem: PrepDetailResponse | PrepListItem | null = detail ?? cached ?? fallbackItem
  const headingId = prepId ? `prep-detail-${prepId}` : undefined
  const isLoadingFallback = loading && !activeItem
  const shouldRenderDetail = loading || Boolean(error) || Boolean(activeItem)
  const resolvedToggleHandler = enableMyPrepActions !== false ? onToggleMyPrep : undefined
  const currentIndex = prepId ? navIds.indexOf(prepId) : -1
  const prevId = currentIndex > 0 ? navIds[currentIndex - 1] : null
  const nextId = currentIndex >= 0 && currentIndex < navIds.length - 1 ? navIds[currentIndex + 1] : null
  const showNav = navIds.length > 0 && Boolean(onNavigate)

  const handleClose = React.useCallback(() => {
    onClose()
  }, [onClose])

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      keepMounted
      ModalProps={{ keepMounted: true, disableRestoreFocus: true }}
      PaperProps={{
        sx: {
          width: { xs: '100vw', sm: 462, md: 700 },
          maxWidth: '100vw',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: shapeBorderRadius,
        },
      }}
    >
      {shouldRenderDetail ? (
        <>
          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            <PrepDetailContent
              item={activeItem}
              loading={isLoadingFallback}
              error={error}
              onClose={handleClose}
              onToggleMyPrep={resolvedToggleHandler}
              onDismiss={onDismiss}
              onMarkUsed={onMarkUsed}
              onArchive={onArchive}
              onAddToShowClock={onAddToShowClock}
              launchedFromMyPrep={launchedFromMyPrep}
              isSuperadmin={isSuperadmin}
              containerRole="dialog"
              headingId={headingId}
              onRefreshCategories={onRefreshCategories}
            />
          </Box>
          {showNav ? (
            <Box
              sx={{
                zIndex: 2,
                bgcolor: 'background.paper',
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Divider />
              <Stack direction="row" spacing={1} sx={{ p: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => prevId && onNavigate?.(prevId)}
                  disabled={!prevId}
                  fullWidth
                >
                  Previous
                </Button>
                <Button
                  variant="contained"
                  onClick={() => nextId && onNavigate?.(nextId)}
                  disabled={!nextId}
                  fullWidth
                >
                  Next
                </Button>
              </Stack>
            </Box>
          ) : null}
        </>
      ) : emptyContent ? (
        emptyContent
      ) : null}
    </Drawer>
  )
}

export default PrepDetailDrawer
